/**
 * Formats a number with comma separators for better readability
 * @param value - The number to format
 * @param options - Optional formatting options
 * @returns Formatted number string with commas
 */
export const formatNumber = (
  value: number | string,
  options?: {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
  },
): string => {
  const num = typeof value === "string" ? parseFloat(value) : value;

  if (isNaN(num)) {
    return "0";
  }

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: options?.minimumFractionDigits ?? 0,
    maximumFractionDigits: options?.maximumFractionDigits ?? 2,
  }).format(num);
};

/**
 * Formats a number with comma separators and no decimal places
 * @param value - The number to format
 * @returns Formatted number string with commas and no decimals
 */
export const formatInteger = (value: number | string): string => {
  return formatNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/**
 * Formats a number with comma separators and 1 decimal place
 * @param value - The number to format
 * @returns Formatted number string with commas and 1 decimal
 */
export const formatDecimal = (value: number | string): string => {
  return formatNumber(value, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
};

/**
 * Formats a number with comma separators and 2 decimal places
 * @param value - The number to format
 * @returns Formatted number string with commas and 2 decimals
 */
export const formatDecimal2 = (value: number | string): string => {
  return formatNumber(value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};
